import type { EntitlementKey, ReOSDatabase, StoredAsset, SubscriptionPlan, WorkspaceEntitlement, WorkspaceSubscription } from "./types";

type PlanEntitlementMap = Record<EntitlementKey, boolean | number>;

const PLAN_ENTITLEMENTS: Record<SubscriptionPlan, PlanEntitlementMap> = {
  free: {
    cloud_sync: false,
    local_photo_ai: false,
    inventory_items: 0,
    workspace_members: 1,
    cloud_storage_mb: 0,
    assistant_model_requests_monthly: 0,
    assisted_marketplace_publishing: false,
    live_marketplace_connections: false,
    live_marketplace_operations: false
  },
  starter: {
    cloud_sync: true,
    local_photo_ai: true,
    inventory_items: 250,
    workspace_members: 1,
    cloud_storage_mb: 5_000,
    assistant_model_requests_monthly: 150,
    assisted_marketplace_publishing: true,
    live_marketplace_connections: false,
    live_marketplace_operations: false
  },
  pro: {
    cloud_sync: true,
    local_photo_ai: true,
    inventory_items: 2500,
    workspace_members: 3,
    cloud_storage_mb: 50_000,
    assistant_model_requests_monthly: 600,
    assisted_marketplace_publishing: true,
    live_marketplace_connections: false,
    live_marketplace_operations: false
  },
  team: {
    cloud_sync: true,
    local_photo_ai: true,
    inventory_items: 10_000,
    workspace_members: 10,
    cloud_storage_mb: 200_000,
    assistant_model_requests_monthly: 1_200,
    assisted_marketplace_publishing: true,
    live_marketplace_connections: false,
    live_marketplace_operations: false
  }
};

export function subscriptionForWorkspace(database: ReOSDatabase, workspaceID: string | undefined) {
  return database.subscriptions.find((subscription) => subscription.workspaceID === workspaceID);
}

export function privateBetaAccessForWorkspace(database: ReOSDatabase, workspaceID: string | undefined, now = new Date()) {
  if (!workspaceID) return undefined;
  const sourceSubscriptionID = `private_alpha_${workspaceID}`;
  const activeEntitlements = database.entitlements
    .filter((entitlement) => entitlement.workspaceID === workspaceID && entitlement.sourceSubscriptionID === sourceSubscriptionID)
    .filter((entitlement) => !entitlement.expiresAt || new Date(entitlement.expiresAt).getTime() > now.getTime());
  if (activeEntitlements.length === 0) return undefined;
  const expiresAt = activeEntitlements
    .flatMap((entitlement) => entitlement.expiresAt ? [entitlement.expiresAt] : [])
    .sort()[0];
  return { expiresAt };
}

export function effectiveEntitlementsForWorkspace(database: ReOSDatabase, workspaceID: string | undefined, now = new Date()): Record<EntitlementKey, boolean | number> {
  const subscription = subscriptionForWorkspace(database, workspaceID);
  const base = { ...PLAN_ENTITLEMENTS[effectivePlanForSubscription(subscription, now)] };
  const ignoredSubscriptionID = subscription && !subscriptionGrantsPaidEntitlements(subscription, now) ? subscription.id : undefined;

  database.entitlements
    .filter((entitlement) => entitlement.workspaceID === workspaceID)
    .filter((entitlement) => !entitlement.expiresAt || new Date(entitlement.expiresAt).getTime() > now.getTime())
    .filter((entitlement) => !ignoredSubscriptionID || entitlement.sourceSubscriptionID !== ignoredSubscriptionID)
    .forEach((entitlement) => {
      base[entitlement.key] = entitlement.value;
    });

  return base;
}

export function hasEntitlement(database: ReOSDatabase, workspaceID: string | undefined, key: EntitlementKey, now = new Date()) {
  return Boolean(effectiveEntitlementsForWorkspace(database, workspaceID, now)[key]);
}

export function limitForEntitlement(database: ReOSDatabase, workspaceID: string | undefined, key: EntitlementKey, now = new Date()) {
  const value = effectiveEntitlementsForWorkspace(database, workspaceID, now)[key];
  return typeof value === "number" ? value : value ? Number.POSITIVE_INFINITY : 0;
}

export function storedAssetCountsTowardUsage(asset: StoredAsset) {
  return !asset.cleanupPendingAt && !asset.replacementForPhotoID;
}

export function storedAssetUsageBytes(database: ReOSDatabase, workspaceID: string | undefined) {
  return database.storedAssets
    .filter((asset) => asset.workspaceID === workspaceID && storedAssetCountsTowardUsage(asset))
    .reduce((total, asset) => total + asset.byteSize, 0);
}

export function storedAssetReplacementCreditBytes(
  database: ReOSDatabase,
  workspaceID: string,
  replacesPhotoID: string | undefined,
  incomingAssetID?: string
) {
  if (!replacesPhotoID) {
    return 0;
  }
  const photo = database.itemPhotos.find((candidate) => candidate.id === replacesPhotoID);
  const item = photo
    ? database.inventoryItems.find((candidate) => candidate.id === photo.itemID && candidate.workspaceID === workspaceID)
    : undefined;
  if (!photo?.assetID || !item || photo.assetID === incomingAssetID) {
    return 0;
  }
  if (database.itemPhotos.filter((candidate) => candidate.assetID === photo.assetID).length !== 1) {
    return 0;
  }
  const asset = database.storedAssets.find(
    (candidate) => candidate.id === photo.assetID && candidate.workspaceID === workspaceID
  );
  if (!asset || (asset.itemID && asset.itemID !== item.id) || !storedAssetCountsTowardUsage(asset)) {
    return 0;
  }
  return Math.max(0, asset.byteSize);
}

export function projectedStoredAssetUsageForUpload(
  database: ReOSDatabase,
  workspaceID: string,
  incoming: {
    assetID: string;
    byteSize: number;
    replacesPhotoID?: string;
  }
) {
  const usedBytes = database.storedAssets
    .filter(
      (asset) =>
        asset.workspaceID === workspaceID &&
        asset.id !== incoming.assetID &&
        storedAssetCountsTowardUsage(asset)
    )
    .reduce((total, asset) => total + asset.byteSize, 0);
  const reservedGrowthBytes = database.storedAssets
    .filter(
      (asset) =>
        asset.workspaceID === workspaceID &&
        asset.id !== incoming.assetID &&
        Boolean(asset.replacementForPhotoID) &&
        !asset.cleanupPendingAt
    )
    .reduce(
      (total, asset) =>
        total +
        Math.max(
          0,
          asset.byteSize -
            storedAssetReplacementCreditBytes(
              database,
              workspaceID,
              asset.replacementForPhotoID,
              asset.id
            )
        ),
      0
    );
  const replacementCreditBytes = storedAssetReplacementCreditBytes(
    database,
    workspaceID,
    incoming.replacesPhotoID,
    incoming.assetID
  );
  const requestedGrowthBytes = incoming.replacesPhotoID
    ? Math.max(0, incoming.byteSize - replacementCreditBytes)
    : incoming.byteSize;

  return {
    usedBytes,
    reservedGrowthBytes,
    requestedGrowthBytes,
    projectedBytes: usedBytes + reservedGrowthBytes + requestedGrowthBytes
  };
}

export function pendingUploadReservedGrowthBefore(
  database: ReOSDatabase,
  workspaceID: string,
  pendingUploads: Array<{
    id: string;
    workspaceID: string;
    assetID: string;
    byteSize?: number;
    replacesPhotoID?: string;
    createdAt: string;
    expiresAt: string;
  }>,
  candidateUploadID: string,
  now: Date
) {
  const candidate = pendingUploads.find(
    (upload) =>
      upload.id === candidateUploadID &&
      upload.workspaceID === workspaceID
  );
  if (!candidate) {
    return 0;
  }
  const candidateOrder = `${candidate.createdAt}\u0000${candidate.id}`;

  return pendingUploads
    .filter(
      (upload) =>
        upload.workspaceID === workspaceID &&
        upload.id !== candidate.id &&
        new Date(upload.expiresAt).getTime() > now.getTime() &&
        `${upload.createdAt}\u0000${upload.id}` < candidateOrder &&
        !database.storedAssets.some(
          (asset) => asset.workspaceID === workspaceID && asset.id === upload.assetID
        )
    )
    .reduce((total, upload) => {
      const byteSize = Math.max(0, upload.byteSize ?? 0);
      if (!upload.replacesPhotoID) {
        return total + byteSize;
      }
      return (
        total +
        Math.max(
          0,
          byteSize -
            storedAssetReplacementCreditBytes(
              database,
              workspaceID,
              upload.replacesPhotoID,
              upload.assetID
            )
        )
      );
    }, 0);
}

export function planEntitlementsFor(plan: SubscriptionPlan): Record<EntitlementKey, boolean | number> {
  return { ...PLAN_ENTITLEMENTS[plan] };
}

export function privateAlphaEntitlementsForWorkspace(
  workspaceID: string,
  timestamp: string,
  expiresAt: string
): WorkspaceEntitlement[] {
  return Object.entries({
    ...PLAN_ENTITLEMENTS.pro,
    live_marketplace_connections: true,
    live_marketplace_operations: false
  }).map(([key, value]) => ({
    id: `entitlement_private_alpha_${workspaceID}_${key}`,
    workspaceID,
    key: key as EntitlementKey,
    value,
    sourceSubscriptionID: `private_alpha_${workspaceID}`,
    expiresAt,
    createdAt: timestamp,
    updatedAt: timestamp
  }));
}

export function defaultSubscriptionForWorkspace(workspaceID: string, timestamp: string, plan: SubscriptionPlan = "starter"): WorkspaceSubscription {
  return {
    id: `subscription_${workspaceID}`,
    workspaceID,
    plan,
    status: "trialing",
    trialEndsAt: addDays(timestamp, 14),
    createdAt: timestamp,
    updatedAt: timestamp
  };
}

export function entitlementsForSubscription(subscription: WorkspaceSubscription): WorkspaceEntitlement[] {
  const timestamp = subscription.updatedAt;
  return Object.entries(PLAN_ENTITLEMENTS[effectivePlanForSubscription(subscription)])
    .filter(([key]) => key !== "live_marketplace_connections" && key !== "live_marketplace_operations")
    .map(([key, value]) => ({
      id: `entitlement_${subscription.workspaceID}_${key}`,
      workspaceID: subscription.workspaceID,
      key: key as EntitlementKey,
      value,
      sourceSubscriptionID: subscription.id,
      createdAt: timestamp,
      updatedAt: timestamp
    }));
}

function effectivePlanForSubscription(subscription: WorkspaceSubscription | undefined, now = new Date()): SubscriptionPlan {
  if (!subscription || !subscriptionGrantsPaidEntitlements(subscription, now)) {
    return "free";
  }
  return subscription.plan;
}

export function subscriptionGrantsPaidEntitlements(subscription: WorkspaceSubscription, now = new Date()) {
  if (subscription.status === "active") {
    return true;
  }
  if (subscription.status !== "trialing") {
    return false;
  }
  if (!subscription.trialEndsAt) {
    return true;
  }
  return new Date(subscription.trialEndsAt).getTime() > now.getTime();
}

function addDays(timestamp: string, days: number) {
  const date = new Date(timestamp);
  date.setDate(date.getDate() + days);
  return date.toISOString();
}
